import { Component, computed, effect, inject, input, linkedSignal, output, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzCardComponent } from 'ng-zorro-antd/card';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzInputDirective, NzInputGroupComponent, NzInputGroupWhitSuffixOrPrefixDirective } from 'ng-zorro-antd/input';
import { NzSpaceComponent, NzSpaceItemDirective } from 'ng-zorro-antd/space';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  HeldPackageCounts,
  ServicePackageService,
  User,
  UserService,
  VisitCommentService,
  heldPackageCounts,
} from '@beautyapp/core';
import { VisitAddFacade } from '@beautyapp/shared';
import { UserUpdateModalComponent } from './user-update-modal/user-update-modal.component';
import { UserSelectionModalV2Component } from './user-selection-modal/user-selection-modal-v2.component';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import { DecimalPipe, UpperCasePipe } from '@angular/common';
import { BirthdayBadgeComponent } from '@beautyapp/customer-360';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { SelectSearchComponent } from '@beautyapp/form';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'beautyapp-user-card',
  imports: [
    FormsModule,
    NzButtonComponent,
    NzCardComponent,
    NzIconDirective,
    NzInputDirective,
    NzInputGroupComponent,
    NzInputGroupWhitSuffixOrPrefixDirective,
    NzSpaceComponent,
    TranslatePipe,
    DecimalPipe,
    NzTooltipDirective,
    NzSpaceItemDirective,
    NzPopconfirmDirective,
    UpperCasePipe,
    BirthdayBadgeComponent,
    NzCheckboxModule,
    SelectSearchComponent,
  ],
  templateUrl: './user-card.component.html',
  styleUrls: ['./user-card.component.scss']
})
export class UserCardComponent {
  user = input<User>();

  /**
   * beaut-545. The visit does not exist yet — this card is on the planning
   * page, where the customer, the SMS option and the "at the customer's
   * request" flag are still being decided and nothing is saved until the visit
   * is.
   *
   * An explicit input, not `!visitAddFacade.visit()`: in the booked view the
   * facade's visit is briefly undefined while it loads, and a click landing in
   * that window would silently take the wrong branch. This way the booked view
   * behaves exactly as it did.
   */
  scheduling = input(false);

  /**
   * beaut-545. Scheduling only: what the planning form currently holds for
   * these two options. Without them the card would open unchecked while the
   * form says "send the SMS", and the first thing the operator saw would be a
   * lie about what is about to happen.
   */
  initialSendNotification = input<boolean | null>(null);
  initialUserRequest = input<boolean | null>(null);

  viewUser = output<User | undefined>();
  /** beaut-545. Scheduling only: create a customer who is not in the base yet. */
  addUser = output<void>();
  verifyUser = output<User | undefined>();
  updateSmsNotification = output<boolean>();
  updateUserRequest = output<boolean>();
  updateUserEvent = output<User | undefined>();
  selectUserEvent = output<User | undefined>();
  refreshVisitEvent = output<number>();

  selectedUser = linkedSignal<User | undefined>(() => this.user());

  loading = signal<boolean>(false);

  loadingUserRequest = signal<boolean>(false);

  visitAddFacade = inject(VisitAddFacade);
  private modalService = inject(NzModalService);
  private translate = inject(TranslateService);
  private userService = inject(UserService);
  private message = inject(NzMessageService);
  private visitCommentService = inject(VisitCommentService);
  private packageService = inject(ServicePackageService);

  /**
   * beaut-545. What the customer search offers: the current search results,
   * plus whoever is already selected when they are not among them.
   *
   * The field keeps showing the chosen customer after the pick, and the results
   * list is replaced on every keystroke — without this the field would lose the
   * name it is displaying and fall back to printing the raw id.
   */
  private customers = toSignal(this.visitAddFacade.customers$, {
    initialValue: [] as any[],
  });

  customerOptions = computed(() => {
    const list = this.customers() ?? [];
    const current = this.selectedUser() as any;
    if (!current?.id) return list;
    return list.some((u: any) => u.id === current.id)
      ? list
      : [current, ...list];
  });

  smsNotification = signal<boolean>(false);
  userRequest = signal<boolean>(false);

  /** Quick communication-language switcher (chip that opens inline). */
  smsLangOptions = ['ka', 'ru', 'en'] as const;
  smsLangOpen = signal<boolean>(false);
  smsLangSaving = signal<boolean>(false);

  userUpdating = signal<boolean>(false);

  // Selected customer's pinned-else-latest comment. One fetch per customer
  // change — guarded so the effect never re-requests for the same id (no N+1).
  latestComment = signal<{ text: string; isPinned: boolean } | null>(null);
  private lastCommentUserId: string | null = null;

  /**
   * The customer's service packages, as the customer profile shows them —
   * counted by the same `heldPackageCounts`, so the two screens cannot differ.
   * Zero for both renders nothing, as there.
   */
  packageCounts = signal<HeldPackageCounts>({ own: 0, gift: 0 });

  /** The id alone, so a refreshed object for the SAME customer (a new balance
   * after a payment) does not re-count; a computed only notifies on change. */
  private selectedUserId = computed(() => this.selectedUser()?.id ?? null);

  constructor() {
    // beaut-545. Seeded from the planning form, which owns these two while the
    // visit does not exist yet.
    effect(() => {
      if (!this.scheduling()) return;
      this.smsNotification.set(this.initialSendNotification() ?? true);
      this.userRequest.set(this.initialUserRequest() ?? false);
    });

    effect(() => {
      if (this.visitAddFacade.visit()) {
        this.smsNotification.set(this.visitAddFacade.visit()?.sendNotification ?? false);
        this.userRequest.set(this.visitAddFacade.visit()?.onUserRequest ?? false);
      }
    })

    // Keyed on the customer the ledger SHOWS (`selectedUser`), which the
    // selection modal can change without the input changing. The cleanup
    // drops a request still in flight for the previous customer, so a slow
    // answer can never paint their count under the new name.
    effect((onCleanup) => {
      const id = this.selectedUserId();
      this.packageCounts.set({ own: 0, gift: 0 });
      if (!id) return;
      const sub = heldPackageCounts(this.packageService, id).subscribe((counts) =>
        this.packageCounts.set(counts)
      );
      onCleanup(() => sub.unsubscribe());
    });

    effect(() => {
      const id = this.user()?.id;
      if (id && id !== this.lastCommentUserId) {
        this.lastCommentUserId = id;
        this.visitCommentService
          .getCustomerLatestComment(id)
          .subscribe((res) => this.latestComment.set(res));
      }
    });
  }

  // `checked` is the checkbox's new value (beaut-605: driven by ngModelChange,
  // not a host click). Defaults to a flip for callers without one.
  clickSwitch(checked = !this.smsNotification()) {
    this.smsNotification.set(checked);

    // beaut-545. No visit to update yet: the planning form holds the answer
    // until it is saved with everything else.
    if (this.scheduling()) {
      this.updateSmsNotification.emit(this.smsNotification());
      return;
    }

    this.loading.set(true);
    this.visitAddFacade.updateSmsNotification(
      this.smsNotification(),
      this.userRequest()
    ).subscribe((res) => {
      this.loading.set(false);
    })
  }

  clickUserRequestSwitch(checked = !this.userRequest()) {
    this.userRequest.set(checked);

    // beaut-545. See clickSwitch().
    if (this.scheduling()) {
      this.updateUserRequest.emit(this.userRequest());
      return;
    }

    this.loadingUserRequest.set(true);
    this.visitAddFacade.updateSmsNotification(
      this.smsNotification(),
      this.userRequest()
    ).subscribe((res) => {
      this.loadingUserRequest.set(false);
    })
  }

  updateUser() {
    if (!this.user()) {
      return;
    }

    const modal = this.modalService.create({
      nzTitle: `${this.translate.instant('users.form.updateUser')} - ${
        this.user()?.fullName || this.translate.instant('users.form.unknownUser')
      }`,
      nzContent: UserUpdateModalComponent,
      nzWidth: '800px',
      nzStyle: { top: '20px' },
      nzData: {
        user: this.user()
      },
      nzFooter: null
    });

    modal.afterClose.subscribe((updatedUser) => {
      if (updatedUser) {
        // Emit the updated user to parent component
        this.updateUserEvent.emit(updatedUser);
      }
    });
  }

  selectUser() {
    const modal = this.modalService.create({
      nzTitle: this.translate.instant('users.form.selectUser'),
      nzContent: UserSelectionModalV2Component,
      nzWidth: '900px',
      nzStyle: { top: '20px' },
      nzFooter: null
    });

    modal.afterClose.subscribe((selectedUser) => {
      if (selectedUser) {
        console.log(selectedUser);
        // beaut-545. Nothing is saved yet, so there is nothing to confirm:
        // the pick IS the choice, and the save/cancel bar would ask the
        // operator to approve a change to a visit that does not exist.
        if (this.scheduling()) {
          this.selectedUser.set(selectedUser);
          this.selectUserEvent.emit(selectedUser);
          return;
        }
        if (selectedUser.id !== this.user()?.id) {
          this.userUpdating.set(true)
          this.selectedUser.set(selectedUser);
        } else {
          this.userUpdating.set(false)
        }
      }
    });
  }

  /**
   * beaut-545. Scheduling only: the customer picked in the inline search.
   * Nothing is saved — the planning form owns the choice until the visit is.
   */
  pickCustomer(user: User) {
    if (!user) return;
    this.selectedUser.set(user);
    this.selectUserEvent.emit(user);
  }

  cancelUserChange() {
    // Undo a pending customer change: restore the original user and leave the
    // "updating" state so nothing is persisted (beaut-298).
    this.selectedUser.set(this.user());
    this.userUpdating.set(false);
  }

  saveUser() {
    if (!this.selectedUser()) {
      return;
    }

    console.log(this.selectedUser());

    // beaut-611. The save answers with the VISIT, not the customer. Keep the
    // customer that was picked: taking the answer put the visit id where a
    // customer id belongs (the package count then asked userId=<visit id>).
    // The visit refresh below brings the full customer record.
    const picked = this.selectedUser() as User;
    this.visitAddFacade.saveUser(picked).subscribe(() => {
      this.userUpdating.set(false)
      this.selectedUser.set(picked);
      this.updateUserEvent.emit(picked);
      this.refreshVisitEvent.emit(this.visitAddFacade.visit().id);
    })
  }

  /**
   * Quick-change the customer's communication (SMS) language and persist it.
   * Sends the customer's existing fields plus the new smsLanguage (users PUT is
   * a full update), mirroring the user-add-edit save shape.
   */
  setSmsLanguage(lang: string) {
    const u = this.selectedUser() as any;
    if (!u || u.smsLanguage === lang || this.smsLangSaving()) {
      this.smsLangOpen.set(false);
      return;
    }
    this.smsLangSaving.set(true);
    const payload = {
      id: u.id,
      cardNo: u.cardNo,
      firstname: u.firstname,
      lastname: u.lastname,
      gender: u.gender,
      mobilePhone: u.mobilePhone,
      phone: u.phone,
      email: u.email,
      identityNumber: u.identityNumber,
      birthDate: u.birthDate,
      description: u.description,
      countryId: u.countryId,
      cityId: u.cityId,
      address: u.address,
      bloodGroup: u.bloodGroup,
      workplace: u.workplace,
      userSourceId: u.userSourceId,
      smsLanguage: lang,
    };
    this.userService.update(payload).subscribe({
      next: () => {
        this.selectedUser.set({ ...u, smsLanguage: lang });
        this.smsLangSaving.set(false);
        this.smsLangOpen.set(false);
        this.message.success(
          this.translate.instant('users.form.smsLanguageSaved')
        );
      },
      error: () => {
        this.smsLangSaving.set(false);
        this.message.error(this.translate.instant('users.form.smsLanguageError'));
      },
    });
  }
}
